import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, MoveHorizontal } from "lucide-react";
import { GALLERY, GALLERY_CATEGORIES } from "@/data/gallery";
import GalleryTile from "@/components/GalleryTile";
import KitBoatTimeline from "@/components/KitBoatTimeline";
import CtaBand from "@/components/CtaBand";
import Seo from "@/components/Seo";
import { FewBubbles } from "@/components/BackgroundFX";

const categorySlug = (category) => category.toLowerCase().replace(/[^a-z]+/g, "-");

const shuffleItems = (items) => [...items].sort(() => Math.random() - 0.5);

export default function GalleryPage() {
  const [searchParams] = useSearchParams();
  const requestedCategory = GALLERY_CATEGORIES.find((c) => categorySlug(c) === searchParams.get("category"));
  const [filter, setFilter] = useState(requestedCategory || "Boats & Marine");
  const isKitBuild = filter === "Kit Boat Builds";
  const orderedItems = useMemo(() => {
    const filtered = GALLERY.filter((g) => g.category === filter);
    if (isKitBuild) return [...filtered].sort((a, b) => a.step - b.step);
    if (filter === "Boats & Marine") {
      const [first, second, ...rest] = filtered;
      return [first, second, ...shuffleItems(rest)];
    }
    return filtered;
  }, [filter, isKitBuild]);

  useEffect(() => {
    if (requestedCategory) setFilter(requestedCategory);
  }, [requestedCategory]);

  return (
    <>
      <Seo
        title="Boat Modification & Fabrication Gallery | SAMI FAB Albany WA"
        description="View SAMI FAB boat modification and aluminium fabrication work, including kit boat builds, marine fit-outs, canopies, toolboxes, trailers and custom storage in Albany WA."
        keywords="boat modification gallery, aluminium fabrication photos, kit boat build process, marine fit-out Albany, custom canopies, aluminium toolboxes, trailer fabrication"
        path="/gallery"
      />
      <section data-testid="gallery-page-header" className="relative overflow-hidden pt-[76px] bg-white border-b border-[#007bff]/15">
        <FewBubbles />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-10">
          <div className="overflow-hidden">
            <motion.p
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
              className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#007bff]"
            >
              Gallery — Sami Fab, Albany WA
            </motion.p>
          </div>
          <div className="overflow-hidden mt-4">
            <motion.h1
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.22 }}
              className="font-display font-black uppercase tracking-tighter text-[#003366] text-4xl sm:text-5xl lg:text-6xl leading-[0.98]"
            >
              Our Work.
            </motion.h1>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
            className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-slate-600"
          >
            Every job cut, welded and finished by hand in Albany — browse by what you’re into.
          </motion.p>
        </div>
      </section>

      {/* Sticky category tab bar — water-fill tabs */}
      <div
        data-testid="gallery-filters"
        className="sticky top-[76px] md:top-[92px] z-40 bg-white/90 backdrop-blur-xl border-b border-[#007bff]/15"
      >
        <p className="md:hidden pt-2 text-center font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-slate-400/80 flex items-center justify-center gap-2">
          <MoveHorizontal size={11} className="text-[#007bff]/60" /> Swipe
        </p>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex gap-2 md:gap-3 overflow-x-auto no-scrollbar py-2.5 md:py-3.5" style={{ scrollPaddingLeft: 16 }}>
            {GALLERY_CATEGORIES.map((c) => {
              const count = GALLERY.filter((g) => g.category === c).length;
              const active = filter === c;
              return (
                <button
                  key={c}
                  onClick={(e) => {
                    setFilter(c);
                    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
                    window.scrollTo(0, 0);
                    e.currentTarget.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
                  }}
                  data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`relative shrink-0 overflow-hidden px-3 md:px-5 py-2 md:py-2.5 border font-mono text-[10px] md:text-xs font-bold uppercase tracking-[0.12em] md:tracking-[0.15em] transition-[color,border-color,transform] duration-300 active:scale-95 ${
                    active
                      ? "border-[#003366] text-white"
                      : "border-[#007bff]/30 text-slate-500 hover:border-[#007bff] hover:text-[#007bff]"
                  }`}
                >
                  {active && (
                    <motion.span
                      layoutId="gallery-tab-water"
                      className="absolute inset-0 bg-[#003366]"
                      transition={{ type: "spring", stiffness: 380, damping: 32 }}
                    >
                      <svg className="tab-wave" viewBox="0 0 2880 160" preserveAspectRatio="none" aria-hidden>
                        <path
                          d="M0,90 C240,50 480,130 720,90 C960,50 1200,130 1440,90 C1680,50 1920,130 2160,90 C2400,50 2640,130 2880,90 L2880,160 L0,160 Z"
                          fill="rgba(0,123,255,0.55)"
                        />
                      </svg>
                      <svg className="tab-wave tab-wave-2" viewBox="0 0 2880 160" preserveAspectRatio="none" aria-hidden>
                        <path
                          d="M0,80 C240,130 480,30 720,80 C960,130 1200,30 1440,80 C1680,130 1920,30 2160,80 C2400,130 2640,30 2880,80 L2880,160 L0,160 Z"
                          fill="rgba(77,163,255,0.45)"
                        />
                      </svg>
                    </motion.span>
                  )}
                  <span className="relative z-10 flex items-center gap-2">
                    {c}
                    <span
                      className={`text-[9px] px-1.5 py-0.5 leading-none transition-colors duration-300 ${
                        active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-400"
                      }`}
                    >
                      {count}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <section className="relative pb-24 bg-white overflow-hidden">
        <div className="grid-drift" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-12">
          {isKitBuild ? (
            <KitBoatTimeline items={orderedItems} />
          ) : (
            <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5 md:gap-6">
              <AnimatePresence mode="popLayout">
                {orderedItems.map((item, i) => (
                  <GalleryTile key={item.src} item={item} layout priority={i < 6} />
                ))}
              </AnimatePresence>
            </motion.div>
          )}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
            className="mt-12 flex flex-wrap items-center justify-center gap-6 text-center"
          >
            <p className="text-slate-600 text-base">Like what you see? Let’s build yours next.</p>
            <Link
              to="/quote"
              data-testid="gallery-free-quote-button"
              className="group inline-flex items-center gap-3 bg-[#343a40] text-white px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#007bff] hover:scale-[1.03] transition-[background-color,transform] duration-200"
            >
              Free Quote
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
